/**
 * AnalyticsManager.js
 * 
 * A highly optimized, zero-allocation (in hot paths), singleton Analytics Manager for WebXR.
 * Uses requestIdleCallback for non-blocking dispatch and Beacon API for guaranteed delivery 
 * on abrupt exits (rage-quits).
 */

// ============================================================================
// ZERO-ALLOCATION ENUMS
// Object.freeze ensures these are immutable. Using them prevents inline string
// allocation during high-frequency combat events.
// ============================================================================
export const TargetType = Object.freeze({
    BOT: 'bot',
    REAL_PLAYER: 'real_player'
});

export const EndReason = Object.freeze({
    COMPLETED: 'completed',
    RAGE_QUIT: 'rage_quit',
    TIMEOUT: 'timeout'
});

export const GameMode = Object.freeze({
    BOT: 'bot',
    REAL_PLAYER: 'real_player'
});

const EventNames = Object.freeze({
    ENTER_VR: 'enter_vr',
    ENTER_DESKTOP: 'enter_desktop',
    XR_HARDWARE_PROFILE: 'xr_hardware_profile',
    FIRST_FIRE: 'first_fire',
    AD_CLICKED: 'ad_clicked',
    ENTER_ARENA_CLICKED: 'enter_arena_clicked',
    MODE_START: 'mode_start',
    TIME_TO_FIRST_ENGAGEMENT: 'time_to_first_engagement',
    MATCH_ENDED: 'match_ended',
    SESSION_END: 'session_end'
});

const EventParams = Object.freeze({
    MODE_TYPE: 'mode_type',
    TARGET_TYPE: 'target_type',
    VALUE: 'value', // Used for time_to_first_engagement
    TOTAL_TIME_SECONDS: 'total_time_seconds',
    TOTAL_SHOTS_FIRED: 'total_shots_fired',
    BOT_HITS: 'bot_hits',
    PLAYER_HITS: 'player_hits',
    TOTAL_KILLS: 'total_kills',
    TOTAL_DEATHS: 'total_deaths',
    MATCH_MODE: 'match_mode',
    AVERAGE_FPS: 'average_fps',
    END_REASON: 'end_reason',
    SESSION_DURATION: 'session_duration',
    HARDWARE_PROFILES: 'hardware_profiles'
});

export class AnalyticsManager {
    static _instance = null;

    /**
     * @returns {AnalyticsManager}
     */
    static get instance() {
        if (!this._instance) {
            this._instance = new AnalyticsManager();
        }
        return this._instance;
    }

    constructor() {
        if (AnalyticsManager._instance) {
            throw new Error("AnalyticsManager is a singleton. Use AnalyticsManager.instance.");
        }

        // ====================================================================
        // CONFIGURATION
        // ====================================================================
        this.isDebug = false; // Set to true to console.table instead of sending to GA4
        
        // ====================================================================
        // HOT PATH AGGREGATION COUNTERS (Strictly primitives)
        // ====================================================================
        this._shotsFired = 0;
        this._botHits = 0;
        this._playerHits = 0;
        this._botKills = 0;
        this._playerKills = 0;
        this._deaths = 0;

        // ====================================================================
        // STATE FLAGS & TIMERS
        // ====================================================================
        this._hasFired = 0; // 0 = false, 1 = true (using int for minor perf gain)
        this._matchStartTime = 0;
        this._sessionStartTime = performance.now();
        this._firstEngagementTime = 0;
        this._matchMode = null;

        // ====================================================================
        // NON-BLOCKING QUEUE SYSTEM
        // ====================================================================
        this._eventQueue = [];
        this._isQueueScheduled = false;
        // Pre-bind to avoid closure allocation during requestIdleCallback scheduling
        this._processQueueBound = this._processQueue.bind(this);
        this._handleExitBound = this._handleExit.bind(this);

        // Register bulletproof exit handlers to catch rage-quits
        window.addEventListener('visibilitychange', this._handleExitBound);
        window.addEventListener('pagehide', this._handleExitBound);
    }

    // ========================================================================
    // ULTRA-HIGH FREQUENCY COMBAT EVENTS (ZERO ALLOCATION HOT PATH)
    // ========================================================================

    logShot() {
        this._shotsFired++;
        
        // Only branches on the first shot of the match. Otherwise, strictly numeric operations.
        if (this._hasFired === 0) {
            this._hasFired = 1;
            this._queueEvent(EventNames.FIRST_FIRE);
        }
    }

    logHit(targetType) {
        if (targetType === TargetType.BOT) {
            this._botHits++;
        } else {
            this._playerHits++;
        }

        // Track first engagement time
        if (this._firstEngagementTime === 0 && this._matchStartTime > 0) {
            this._firstEngagementTime = performance.now() - this._matchStartTime;
            this._queueEvent(EventNames.TIME_TO_FIRST_ENGAGEMENT, { 
                [EventParams.VALUE]: this._firstEngagementTime / 1000 
            });
        }
    }

    logKill(targetType) {
        if (targetType === TargetType.BOT) {
            this._botKills++;
        } else {
            this._playerKills++;
        }
    }

    logDeath() {
        this._deaths++;
    }

    // ========================================================================
    // MATCH STATE MANAGEMENT
    // ========================================================================

    logMatchStart(modeType) {
        this._resetMatchCounters();
        this._matchMode = modeType;
        this._matchStartTime = performance.now();
        
        this._queueEvent(EventNames.MODE_START, { 
            [EventParams.MODE_TYPE]: modeType 
        });
    }

    /**
     * Flushes aggregated combat data as a single payload and resets counters.
     * @param {string} endReason - Use EndReason enum
     * @param {number} averageFps - Engine-calculated average FPS
     * @param {boolean} forceBeacon - Internal flag used during page unload
     */
    logMatchEnd(endReason, averageFps = 0, forceBeacon = false) {
        if (this._matchStartTime === 0) return; // No match active

        const totalTimeSecs = (performance.now() - this._matchStartTime) / 1000;

        const payload = {
            [EventParams.TOTAL_TIME_SECONDS]: totalTimeSecs,
            [EventParams.TOTAL_SHOTS_FIRED]: this._shotsFired,
            [EventParams.BOT_HITS]: this._botHits,
            [EventParams.PLAYER_HITS]: this._playerHits,
            [EventParams.TOTAL_KILLS]: this._botKills + this._playerKills,
            [EventParams.TOTAL_DEATHS]: this._deaths,
            [EventParams.MATCH_MODE]: this._matchMode,
            [EventParams.AVERAGE_FPS]: averageFps,
            [EventParams.END_REASON]: endReason
        };

        this._dispatchNow(EventNames.MATCH_ENDED, payload, forceBeacon);
        this._resetMatchCounters();
    }

    _resetMatchCounters() {
        this._shotsFired = 0;
        this._botHits = 0;
        this._playerHits = 0;
        this._botKills = 0;
        this._playerKills = 0;
        this._deaths = 0;
        this._hasFired = 0;
        this._firstEngagementTime = 0;
        this._matchStartTime = 0;
        this._matchMode = null;
    }

    // ========================================================================
    // LOW FREQUENCY UI/SESSION EVENTS (DEFERRED VIA QUEUE)
    // ========================================================================

    logEnterVR(profilesArray = []) {
        this._queueEvent(EventNames.ENTER_VR);
        if (profilesArray.length > 0) {
            this._queueEvent(EventNames.XR_HARDWARE_PROFILE, { 
                [EventParams.HARDWARE_PROFILES]: profilesArray.join(',') 
            });
        }
    }

    logEnterDesktop() {
        this._queueEvent(EventNames.ENTER_DESKTOP);
    }

    logAdClicked() {
        this._queueEvent(EventNames.AD_CLICKED);
    }

    logEnterArenaClicked() {
        this._queueEvent(EventNames.ENTER_ARENA_CLICKED);
    }

    // ========================================================================
    // INTERNAL DISPATCH & QUEUE MECHANICS
    // ========================================================================

    _queueEvent(eventName, params = undefined) {
        this._eventQueue.push({ eventName, params });
        
        if (!this._isQueueScheduled) {
            this._isQueueScheduled = true;
            // Fallback to setTimeout if requestIdleCallback is unsupported (e.g., Safari)
            const scheduleIdle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
            scheduleIdle(this._processQueueBound);
        }
    }

    _processQueue(deadline) {
        this._isQueueScheduled = false;
        // Process events while we have idle time (or exhaust the queue if using setTimeout fallback)
        while (this._eventQueue.length > 0 && (!deadline || deadline.timeRemaining() > 0)) {
            const evt = this._eventQueue.shift();
            this._dispatchNow(evt.eventName, evt.params, false);
        }
        
        // If queue not empty, reschedule
        if (this._eventQueue.length > 0) {
            this._isQueueScheduled = true;
            const scheduleIdle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
            scheduleIdle(this._processQueueBound);
        }
    }

    _dispatchNow(eventName, params = {}, useBeacon = false) {
        if (useBeacon) {
            // GA4 utilizes transport_type: 'beacon' to guarantee delivery during page unload
            params.transport_type = 'beacon';
        }

        if (this.isDebug) {
            console.log(`[AnalyticsManager] Event: ${eventName}`);
            if (Object.keys(params).length > 0) console.table(params);
            return;
        }

        if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
            window.gtag('event', eventName, params);
        }
    }

    // ========================================================================
    // BULLETPROOF EXIT HANDLING
    // ========================================================================

    _handleExit(e) {
        // Ensure we only trigger this when the page is actually hiding/unloading
        if (document.visibilityState === 'hidden' || e.type === 'pagehide') {
            
            // 1. Force flush an ongoing match as a rage-quit
            if (this._matchStartTime > 0) {
                this.logMatchEnd(EndReason.RAGE_QUIT, 0, true);
            }

            // 2. Track total session duration
            const sessionDurationSecs = (performance.now() - this._sessionStartTime) / 1000;
            this._dispatchNow(EventNames.SESSION_END, { 
                [EventParams.SESSION_DURATION]: sessionDurationSecs 
            }, true);

            // 3. Synchronously drain anything left in the idle queue using the beacon transport
            while (this._eventQueue.length > 0) {
                const evt = this._eventQueue.shift();
                this._dispatchNow(evt.eventName, evt.params, true);
            }
        }
    }
}